import {
  Box,
  Button,
  Collapse,
  Divider,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {ChevronDownIcon, ChevronUpIcon} from '@chakra-ui/icons';
import NiceModal from '@ebay/nice-modal-react';
import React, {useMemo} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ItemCatalogue2StateFF, isItemStateCatalogueFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {ProfilUBItem} from 'api-types/ffconnect';
import {HaliRenderAll, useHalState} from '@synako/haligator';
import {useDisclosure} from '@chakra-ui/react';
import {useClient} from '@/haligator-factory';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast.tsx';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire.tsx';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks.tsx';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {ArticleBlock} from '@/domains/panier/PanierFound/ArticleBlock.tsx';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';
import {ProductSkeleton} from '@/backoffice/domains/PriseDeCommande/AjoutArticle/ProductSkeletonList';
import {DisplayStatusChip} from '@/components/ub/StatusDisplay';
import {HEADER_HEIGHT} from '@/constants/layout';
import {SidePanelProduitsUB} from './SidePanelProduitsUB';

/**
 * Section accordéon pour un item UB : header avec nom + statut + count,
 * body avec les articles du catalogue rapprochés via `item.articles[]` (codes).
 * Réutilise `LigneProduitCommune` pour le rendu des lignes (cohérent avec
 * cadencier / listes / saison / OC).
 */
export const MesProduitsUBSectionItem: React.FC<{
  item: ProfilUBItem;
  lignesDePanier: State<LigneDePanierDesc>[];
  categoriesAvecProduits: Set<string> | undefined;
  motsDirecteursAvecProduits: Set<string> | undefined;
  /**
   * Offset sticky (sous Navbar, ou sous filtres sticky en md+). Sert aussi de
   * `scrollMarginTop` à la section, pour le clic sur un tag.
   */
  stickyTop?: string | Record<string, string>;
}> = React.memo(
  ({
    item,
    lignesDePanier,
    categoriesAvecProduits,
    motsDirecteursAvecProduits,
    stickyTop = HEADER_HEIGHT,
  }) => {
    const {t} = useTranslation();
    const {isOpen, onToggle} = useDisclosure({defaultIsOpen: true});
    const clientNav = useClient();
    const codes = useMemo(() => item.articles ?? [], [item.articles]);

    // Lien "Voir tous les produits de l'UB" affiché seulement si l'UB a au moins
    // un produit au catalogue FF (sinon le side panel serait vide).
    const aDesProduitsAuCatalogue =
      (categoriesAvecProduits !== undefined &&
        item.sous_sous_cat_std.some((cat) =>
          categoriesAvecProduits.has(cat),
        )) ||
      (motsDirecteursAvecProduits !== undefined &&
        (item.mots_directeurs ?? []).some((mot) =>
          motsDirecteursAvecProduits.has(mot),
        ));

    const itemCatalogueNavs = useMemo(
      () =>
        codes.map((code) =>
          clientNav.follow('go:item_catalogue_2', {marketplace_id: 'FF', code}),
        ),
      [codes, clientNav],
    );

    return (
      <VStack
        alignItems='stretch'
        width='100%'
        pt='12px'
        pb='0'
        px='0'
        id={`ub-section-${item.id}`}
        // Le clic sur un tag (`scrollIntoView`) arrête la section sous la Navbar
        // et la barre sticky, au même niveau que son header sticky.
        scrollMarginTop={stickyTop}
      >
        <HStack
          bg='gray.light_bg'
          py='6px'
          px='12px'
          mx='12px'
          borderRadius='8px'
          height='41px'
          // Sticky : sous la Navbar, ou sous le bloc filtres+tags sticky (md+)
          position='sticky'
          top={stickyTop}
          zIndex='1'
        >
          <Text
            fontWeight='600'
            fontSize='sm'
          >
            {item.nom}
          </Text>
          <DisplayStatusChip
            status={item.statut}
            label={t(
              `client.mes_produits.ub_essentielles.section_statut.${item.statut ?? 'a-gagner'}`,
            )}
            tooltip={
              <Trans t={t}>
                {t(
                  `client.mes_produits.ub_essentielles.section_regle.${item.statut ?? 'a-gagner'}`,
                )}
              </Trans>
            }
          />
          {aDesProduitsAuCatalogue && (
            <Button
              variant='link'
              color='gray.dark'
              textDecoration='underline'
              size='xs'
              fontWeight='600'
              onClick={() =>
                NiceModal.show(SidePanelProduitsUB, {
                  ub: {
                    nom: item.nom,
                    categories: item.sous_sous_cat_std,
                    mots_directeurs: item.mots_directeurs,
                  },
                })
              }
            >
              {t('client.mes_produits.ub_essentielles.voir_tous_les_produits')}
            </Button>
          )}
          <Box ml='auto'>
            <Button
              variant='ghost'
              p='0'
              onClick={onToggle}
            >
              {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Button>
          </Box>
        </HStack>
        <Collapse in={isOpen}>
          {/* Aucun article : on n'affiche rien (header seul), pas de "Pas de produit". */}
          {codes.length > 0 && (
            <HaliRenderAll
              all={itemCatalogueNavs}
              fallback={() => null}
              notFound={() => null}
              render={({states: itemStates}) => {
                const filtered = itemStates.filter(
                  (s): s is State<ItemCatalogue2Desc> => !!s,
                );
                if (filtered.length === 0) {
                  return null;
                }
                return (
                  <Box
                    marginBottom='1px'
                    px='12px'
                  >
                    {filtered.map((itemState) => (
                      <React.Fragment key={itemState.data.code}>
                        <UBLigne
                          itemState={itemState}
                          ligneDePanier={lignesDePanier.find(
                            (l) => l.data.code_article === itemState.data.code,
                          )}
                        />
                        <Divider borderColor='gray.light' />
                      </React.Fragment>
                    ))}
                  </Box>
                );
              }}
            />
          )}
        </Collapse>
      </VStack>
    );
  },
);

const UBLigne: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo(({itemState, ligneDePanier}) => {
  if (!isItemStateCatalogueFF(itemState)) return null;
  return (
    <UBLigneFFContent
      itemState={itemState}
      ligneDePanier={ligneDePanier}
    />
  );
});

const UBLigneFFContent: React.FC<{
  itemState: ItemCatalogue2StateFF;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo(({itemState, ligneDePanier}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const {state: articleState, loading: articleLoading} = useHalState(
    itemState.follow('article'),
  );
  const code_article = itemState.data.code;

  const precommande = itemState.data.tolede.precommande;
  // Hors panier : la catégorie vient du catalogue, pas de la ligne. Celle-ci
  // n'est passée que pour une info : un prix saisi ou bloqué par le commercial,
  // qui prend alors la place du tag de catégorie.
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    itemState.data.tolede.prix_par_livraison?.[0]?.categorie,
    {contexte: 'catalogue'},
  );
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();

  const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
    textToast,
    'ub_essentielles',
    itemState,
    ligneDePanier,
  );
  const {
    ajouterAvecPrixSaisi,
    isAddingAvecPrix,
    saisieAvantAjout,
    codeArticle,
  } = useAjouterAvecPrixSaisi(itemState, commandeEnColis);

  const prixBlock = (
    <PrixBlockUnitaire
      prixUnitaire={itemState.data.prix_unitaire_ht}
      prixUnitaireTtc={itemState.data.prix_unitaire_ttc}
      uniteFacturation={itemState.data.unite_de_facturation}
      tauxTVA={itemState.data.tva}
      typePrix={itemState.data.tolede.prix_par_livraison![0].type}
      categorieDeTarif={categorieDeTarif}
      modeTarifaireSouhaite={modeTarifaireSouhaite}
      ligneDePanier={ligneDePanier}
      saisieAvantAjout={saisieAvantAjout}
      ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
      codeArticle={codeArticle}
      onEditionPrixChange={onEditionPrixChange}
    />
  );

  if (articleLoading) {
    return <ProductSkeleton />;
  }
  if (!articleState) {
    return (
      <Text>
        {t('operation_commerciale.article_hors_base', {code_article})}
      </Text>
    );
  }

  const articleBlock = (
    <ArticleBlock
      code_article={code_article}
      libelles_tronques
      ligneDePanier={ligneDePanier}
      prixDraft={prixDraft}
    />
  );

  return (
    <LigneProduitCommune
      itemState={itemState}
      articleBlock={articleBlock}
      prixBlock={prixBlock}
      uniteQuantiteBlock={uniteQuantiteBlock}
      aPeser={articleState.data.tolede.a_peser}
      buyBlock={buyBlock}
      somethingSubmitting={isAddingAvecPrix}
      precommande={precommande}
      ligneDePanier={ligneDePanier}
      afficher_suggestions
    />
  );
});
